import { useState, useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import { useDashboardCalculations } from './useDashboardCalculations'
import { useFegliCalculations } from './useFegliCalculations'
import { useSocialSecurityCalculations } from './useSocialSecurityCalculations'
import fegliLogo from 'assets/images/fegliLogo.png'
import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  differenceInDays,
  differenceInYears,
  subMonths,
  add,
  isBefore,
} from 'date-fns'
function createData(
  name,
  paycheck,
  working,
  retirement,
  difference,
  img = '',
  symbol = '',
  infoText = '',
  type = ''
) {
  return {
    name,
    paycheck,
    working,
    retirement,
    difference,
    img,
    symbol,
    infoText,
    type,
  }
}
function findOptimalSickLeave(numberSet, target) {
  return Math.max(...numberSet.filter((num) => num <= target), 0)
}
function getDateDays(date) {
  const newDate = new Date(date)
  return (
    (newDate.getFullYear() * 12 + newDate.getMonth() + 1) * 30 +
    newDate.getDate() +
    1
  )
}
function getFRA(year) {
  if (year < 1938) return 65
  if (year > 1960) return 67

  const table = {
    1938: 65.2,
    1939: 65.3,
    1940: 65.5,
    1941: 65.7,
    1942: 65.8,
    1943: 66.0,
    1944: 66.0,
    1945: 66.0,
    1946: 66.0,
    1947: 66.0,
    1948: 66.0,
    1949: 66.0,
    1950: 66.0,
    1951: 66.0,
    1952: 66.0,
    1953: 66.0,
    1954: 66.0,
    1955: 66.2,
    1956: 66.3,
    1957: 66.5,
    1958: 66.7,
    1959: 66.8,
    1960: 67.0,
  }

  return table[year] || 67
}
function reorderDeductions(objects, order, key) {
  const orderMap = new Map(order.map((item, index) => [item, index]))
  return objects.sort(
    (a, b) =>
      (orderMap.get(a[key]) ?? Infinity) - (orderMap.get(b[key]) ?? Infinity)
  )
}
export const useRetirementGapCalculations = (
  deductions = [],
  liabilities = [],
  incomes = [],
  expenses = [],
  timeFactor = 1
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { intakeState } = useContext(IntakeContext)
  const isGovernmentEmployee = intakeState.governmentEmployee
  const { basicLife, optionA, optionB, optionC } = useFegliCalculations()
  const {
    personalDetailsCurrentAge,
    eligibilityScenariosFirstFullyEligibleDate,
    netIncomeTodayCalculated,
    gapCalculated,
    netIncomeInRetirementCalculated,
    atRetirementGrossIncome,
    eligibilityScenariosModeledCYSinRetirement,
    socialSecurityEstimatedSocialSecurityatFRA,
    socialSecurityEstimatedSocialSecurityatAge62,
    socialSecuritySocialSecurityBenefit,
    FERSYearsOfService,
    atRetirementBasicLife,
    atRetirementIncomeTaxes,
    atRetirementGrossPension,
    atRetirementFERSSupplement,
    atRetirementSocialSecurity,
    atRetirementSSTaxes,
    atRetirementFEHB,
    creditableYearsOfServiceAtRetirement,
    survivorBenefitCost,
    atRetirementVERA,
    veraTypeAndEligible,
    retirementAgeLessThanMRA,
    formerSpouseOwedPensionPercentage,
    firstFullyEligibleDate,
    eligibilityScenarioMRA10Date,
    clientRetirementAge,
  } = useDashboardCalculations(deductions)
  const { calculateSSBenefit, getFRA } = useSocialSecurityCalculations()
  const { deductionTypes } = selectData
  const deductionSubTypeLabelMap = deductionTypes.reduce((summ, type) => {
    summ[type.value] = type.label
    return summ
  }, {})
  const fehbLookup = intakeState.fehbCode
    ? intakeState.fehbCode.replace('HP ', '').match(/^\d/)
      ? '_' + intakeState.fehbCode.replace('HP ', '')
      : intakeState.fehbCode
    : ''
  const healthcareLogo = selectData.fehb[fehbLookup]?.logo || ''

  const plannedRetirementMRA10 =
    new Date(intakeState?.dateOfPlannedRetirement) >=
    new Date(eligibilityScenarioMRA10Date || new Date())
  // console.log('plannedRetirementMRA10', plannedRetirementMRA10)

  const plannedRetirementFullyEligible =
    new Date(intakeState?.dateOfPlannedRetirement) >=
    new Date(firstFullyEligibleDate || new Date())

  const showPension =
    (plannedRetirementMRA10 ||
      plannedRetirementFullyEligible ||
      veraTypeAndEligible) &&
    isGovernmentEmployee
  const veraRetirementType =
    intakeState?.retirementType === 'vera' ||
    intakeState?.retirementType === 'dsr'
  // console.log('veraTypeAndEligible', veraTypeAndEligible)

  try {
    const govSSTaxAmount = 160200
    const ssTaxPercent = 0.062
    const medicareTaxPercent = 0.0145
    const estimatedTaxRate = intakeState?.taxRate
      ? intakeState.taxRate / 100
      : 0.2
    // Create Pension Lookup Table to define
    const pensionAdjuster = new Date(intakeState?.dateOfHire)
    const pensionContribution =
      pensionAdjuster < new Date('2013-1-1')
        ? 0.008
        : pensionAdjuster < new Date('2014-1-1')
        ? 0.031
        : 0.044

    const birthYear = new Date(intakeState?.dateOfBirth).getFullYear()
    const minimumRetirementAge =
      birthYear < 1948
        ? 55
        : birthYear === 1948
        ? 55.1667
        : birthYear === 1949
        ? 55.333
        : birthYear === 1950
        ? 55.5
        : birthYear === 1951
        ? 55.667
        : birthYear === 1952
        ? 55.833
        : birthYear >= 1953 && birthYear <= 1964
        ? 56
        : birthYear === 1965
        ? 56.1667
        : birthYear === 1966
        ? 56.333
        : birthYear === 1967
        ? 56.5
        : birthYear === 1968
        ? 56.667
        : birthYear === 1969
        ? 56.833
        : 57

    // Initial Sources and Deductions
    const baseSalary = (intakeState.baseSalary / 12) * timeFactor
    const baseSalaryPaycheck = (intakeState.baseSalary / 2087) * 80
    const workingNetTakeHome = intakeState?.netTakeHome
      ? (((intakeState?.netTakeHome / 80) * 2087) / 12) * timeFactor
      : 0
    const estiamtedSocialSecurity = intakeState.estimatedSocialSecurity
    const paycheckSocialSecurity = intakeState?.alreadyCollectingSocialSecurity
      ? ((estiamtedSocialSecurity * 12) / 2087) * 80
      : 0
    // change this Social Security benefit to inputted value if they are already collecting

    const clientBirthYear = new Date(
      intakeState?.dateOfBirth || new Date()
    ).getFullYear()
    const clientFRA = getFRA(clientBirthYear || 1960)
    const claimDate =
      intakeState?.dateOfBirth &&
      intakeState?.ageToBeginCollectingSS != null &&
      intakeState?.ageToBeginCollectingSSMonths != null
        ? add(new Date(intakeState.dateOfBirth), {
            years: intakeState.ageToBeginCollectingSS,
            months: intakeState.ageToBeginCollectingSSMonths,
          })
        : null

    const plannedRetirementDate = intakeState?.dateOfPlannedRetirement
      ? new Date(intakeState.dateOfPlannedRetirement)
      : null

    let estimatedSocialSecurityBenefit

    const hasModelInputs =
      intakeState?.socialSecurityModeledAmount != null &&
      intakeState?.socialSecurityModel != null &&
      intakeState?.ageToBeginCollectingSS != null &&
      intakeState?.ageToBeginCollectingSSMonths != null

    if (
      claimDate &&
      plannedRetirementDate &&
      isBefore(plannedRetirementDate, claimDate)
    ) {
      estimatedSocialSecurityBenefit = 0
    } else if (hasModelInputs) {
      let modeledSocialSecurity = calculateSSBenefit({
        fraAge: clientFRA || 67,
        baseIncome: intakeState?.socialSecurityModeledAmount || 2400,
        baseType: intakeState?.socialSecurityModel || 'fullRetirementAge',
        claimAgeYears: intakeState?.ageToBeginCollectingSS || 67,
        claimAgeMonths: intakeState?.ageToBeginCollectingSSMonths || 0,
        birthYear: clientBirthYear,
      })

      // If planned retirement is after claim date, grow by 2.6% per year
      if (plannedRetirementDate && claimDate) {
        const yearsAfterClaim = Math.max(
          0,
          differenceInYears(plannedRetirementDate, claimDate)
        )
        modeledSocialSecurity *= Math.pow(1.026, yearsAfterClaim)
      }

      estimatedSocialSecurityBenefit = modeledSocialSecurity * timeFactor
    } else {
      estimatedSocialSecurityBenefit =
        (atRetirementSocialSecurity / 12) * timeFactor
    }
    // const estimatedSocialSecurityBenefit = (atRetirementSocialSecurity / 12) * timeFactor
    const estimatedFERSSupplement = atRetirementFERSSupplement * timeFactor
    const estimatedGrossPension = isGovernmentEmployee
      ? (atRetirementGrossPension / 12) * timeFactor
      : 0
    const grossIncomeWorking =
      (intakeState.baseSalary / 12) * timeFactor +
      (intakeState?.alreadyCollectingSocialSecurity
        ? estimatedSocialSecurityBenefit
        : 0)
    const fersSupplementorSocialSecurity =
      atRetirementVERA && retirementAgeLessThanMRA
        ? 0
        : atRetirementFERSSupplement === 0
        ? estimatedSocialSecurityBenefit
        : estimatedFERSSupplement
    const fersSupplementorSocialSecurityTaxRate =
      atRetirementFERSSupplement === 0
        ? estimatedTaxRate * 0.85
        : estimatedTaxRate
    const grossIncomeRetirement =
      fersSupplementorSocialSecurity + estimatedGrossPension
    const grossIncomeDifference =
      0 - baseSalary + (isGovernmentEmployee ? grossIncomeRetirement : 0)

    // Normalized Gross Components
    const ssTaxPaycheckBaseSalaryAmount =
      (Math.min(
        intakeState.baseSalary * ssTaxPercent,
        govSSTaxAmount * ssTaxPercent
      ) /
        2087) *
      80
    const ssTaxBaseSalaryAmount =
      (Math.min(
        intakeState.baseSalary * ssTaxPercent,
        govSSTaxAmount * ssTaxPercent
      ) /
        12) *
      timeFactor
    const medicareTaxPaycheckBaseSalaryAmount =
      ((intakeState.baseSalary * medicareTaxPercent) / 2087) * 80
    const medicareTaxBaseSalaryAmount =
      ((intakeState.baseSalary * medicareTaxPercent) / 12) * timeFactor
    const pensionPaycheckContributionAmount =
      ((intakeState.baseSalary * pensionContribution) / 2087) * 80
    const pensionContributionAmount =
      ((intakeState.baseSalary * pensionContribution) / 12) * timeFactor
    const tspPaycheckContributionAmount =
      ((intakeState.baseSalary * (intakeState.tspContribution / 100)) / 2087) *
      80
    const tspContributionAmount =
      ((intakeState.baseSalary * (intakeState.tspContribution / 100)) / 12) *
      timeFactor

    // Normalized Gross Income
    const normalizedAdjustmentsPaycheck =
      ssTaxPaycheckBaseSalaryAmount +
      medicareTaxPaycheckBaseSalaryAmount +
      pensionPaycheckContributionAmount +
      tspPaycheckContributionAmount

    const littleGPaycheckAmount =
      (intakeState.baseSalary / 2087) * 80 - normalizedAdjustmentsPaycheck
    const normalizedAdjustmentsWorking =
      ssTaxBaseSalaryAmount +
      medicareTaxBaseSalaryAmount +
      pensionContributionAmount +
      tspContributionAmount

    //Update
    const littleGAmount =
      (intakeState.baseSalary / 12) * timeFactor - normalizedAdjustmentsWorking
    const normalizedGrossIncomeRetirement = grossIncomeRetirement

    // additional income taxation
    const workingIncomesForTaxes = [
      ...incomes
        .filter((item) => {
          if (item.subType === 'vadisability') return
          if (!item.ieStartDate) return true
          const today = new Date()
          const startDate = new Date(item.ieStartDate)
          return startDate <= today
        })
        .map((item) => {
          const { amount, frequency, type, monthlyPayment } = item
          const adjustedPerMonth =
            frequency === 'monthly'
              ? type === 'assetIncome'
                ? monthlyPayment || 0
                : amount
              : (type === 'assetIncome' ? monthlyPayment || 0 : amount) / 12
          return {
            ...item,
            taxData: adjustedPerMonth,
          }
        }),
    ]
    const retirementIncomesForTaxes = [
      ...incomes
        .filter((item) => {
          if (item.subType === 'vadisability') return
          if (!item.ieStartDate) return true
          const retirementDate = new Date(plannedRetirementDate)
          const startDate = new Date(item.ieStartDate)
          const today = new Date()
          return startDate <= retirementDate || startDate <= today
        })
        .map((item) => {
          const { amount, frequency, type, monthlyPayment } = item
          const adjustedPerMonth =
            frequency === 'monthly'
              ? type === 'assetIncome'
                ? monthlyPayment || 0
                : amount
              : (type === 'assetIncome' ? monthlyPayment || 0 : amount) / 12
          return {
            ...item,
            taxData: adjustedPerMonth,
          }
        }),
    ]
    const workingAdditionalIncomeTaxesTotal = workingIncomesForTaxes.reduce(
      (sum, { taxData }) => sum + Number(taxData),
      0
    )
    const retiredAdditionalIncomeTaxesTotal = retirementIncomesForTaxes.reduce(
      (sum, { taxData }) => sum + Number(taxData),
      0
    )

    // Other Deductions
    const healthInsurancePaycheckCost = intakeState?.healthInsurancePremium
    const healthInsuranceCost =
      ((intakeState?.healthInsurancePremium / 80) * 2087) / 12
    const healthInsuranceRetirementCost =
      intakeState?.healthInsurancePremiumRetirement
        ? intakeState.healthInsurancePremiumRetirement
        : ((intakeState?.healthInsurancePremium / 80) * 2087) / 12
    const basicLifePaycheckCost = ((basicLife?.premium * 12) / 2087) * 80
    const basicLifeCost = basicLife?.premium
    const basicLifeRetirementCost = basicLife?.retirementPremium
    const optionAPaycheckCost = ((optionA?.premium * 12) / 2087) * 80
    const optionACost = optionA?.premium
    const optionARetirementCost = optionA?.retirementPremium
    const optionBPaycheckCost = ((optionB?.premium * 12) / 2087) * 80
    const optionBCost = optionB?.premium
    const optionBRetirementCost = optionB?.retirementPremium
    const optionCPaycheckCost = ((optionC?.premium * 12) / 2087) * 80
    const optionCCost = optionC?.premium
    const optionCRetirementCost = optionC?.retirementPremium
    const estimatedTaxesPaycheckWhileWorking =
      littleGPaycheckAmount * estimatedTaxRate
    const estimatedTaxesWhileWorking =
      (littleGAmount + workingAdditionalIncomeTaxesTotal) * estimatedTaxRate

    const estimatedTaxesInRetirement =
      (estimatedGrossPension -
        survivorBenefitCost +
        retiredAdditionalIncomeTaxesTotal) *
        estimatedTaxRate +
      fersSupplementorSocialSecurity * fersSupplementorSocialSecurityTaxRate

    const estimatedTaxesDifference =
      estimatedTaxesInRetirement - estimatedTaxesWhileWorking

    const agencyShortcode =
      selectData.agency.find(({ value }) => value === intakeState.agency)
        ?.shortcode + ' ' || ''

    const pensionLabelHelpText =
      formerSpouseOwedPensionPercentage > 0 ? 'Divorce Adjusted' : ''

    const initialSources = [
      createData(
        'Current Base Salary',
        baseSalaryPaycheck,
        baseSalary,
        0,
        0 - baseSalary
      ),
      // move pension down below and only show if above FRA
      // createData(
      //   `${agencyShortcode}Pension`,
      //   0,
      //   0,
      //   estimatedGrossPension,
      //   estimatedGrossPension,
      //   'https://upload.wikimedia.org/wikipedia/commons/0/0a/Seal_of_the_United_States_Office_of_Personnel_Management.svg',
      //   '',
      //   pensionLabelHelpText
      // ),
      // createData(
      //   'Gross Income',
      //   baseSalaryPaycheck,
      //   grossIncomeWorking,
      //   grossIncomeRetirement,
      //   grossIncomeDifference
      // ),
    ]
    const pensionAmount =
      showPension &&
      initialSources.push(
        createData(
          `${agencyShortcode}Pension`,
          0,
          0,
          estimatedGrossPension,
          estimatedGrossPension,
          'https://upload.wikimedia.org/wikipedia/commons/0/0a/Seal_of_the_United_States_Office_of_Personnel_Management.svg',
          '',
          pensionLabelHelpText
        )
      )
    const socialSecurity =
      atRetirementFERSSupplement === 0 &&
      initialSources.push(
        createData(
          intakeState?.alreadyCollectingSocialSecurity
            ? 'Social Security'
            : 'Estimated Gross Social Security',
          paycheckSocialSecurity,
          intakeState?.alreadyCollectingSocialSecurity
            ? estimatedSocialSecurityBenefit
            : 0,
          estimatedSocialSecurityBenefit,
          estimatedSocialSecurityBenefit,
          'https://upload.wikimedia.org/wikipedia/commons/2/26/US-SocialSecurityAdmin-Seal.svg'
        )
      )
    const fersSupplement =
      atRetirementFERSSupplement > 0 &&
      initialSources.push(
        createData(
          atRetirementVERA && retirementAgeLessThanMRA
            ? 'FERS Supplement**'
            : 'FERS Supplement',
          0,
          0,

          atRetirementVERA && retirementAgeLessThanMRA
            ? 0
            : estimatedFERSSupplement,
          atRetirementVERA && retirementAgeLessThanMRA
            ? 0
            : estimatedFERSSupplement,
          'https://upload.wikimedia.org/wikipedia/commons/0/0a/Seal_of_the_United_States_Office_of_Personnel_Management.svg'
        )
      )

    const totalTaxes = [
      createData(
        'Social Security Taxes',
        ssTaxPaycheckBaseSalaryAmount,
        ssTaxBaseSalaryAmount,
        0,
        0 - ssTaxBaseSalaryAmount
      ),
      createData(
        'Medicare Taxes',
        medicareTaxPaycheckBaseSalaryAmount,
        medicareTaxBaseSalaryAmount,
        0,
        0 - medicareTaxBaseSalaryAmount
      ),
      createData(
        `State and Federal Taxes (${
          intakeState?.taxRate ? intakeState.taxRate : '20'
        }%)`,
        estimatedTaxesPaycheckWhileWorking,
        estimatedTaxesWhileWorking,
        estimatedTaxesInRetirement,
        estimatedTaxesDifference,
        'https://upload.wikimedia.org/wikipedia/commons/0/0e/Internal_Revenue_Service_logo.svg',
        '-'
      ),
    ]

    const taxesTotals = totalTaxes.reduce(
      (summ, deduction) => {
        summ.paycheck += +deduction.paycheck
        summ.working += +deduction.working
        summ.retirement += +deduction.retirement
        summ.difference += +deduction.difference
        return summ
      },
      { paycheck: 0, working: 0, retirement: 0, difference: 0 }
    )

    const otherDeductions = [
      ...deductions.map((item) => {
        const { amount, subType, keepInRetirement } = item
        const retirementAmount = keepInRetirement && showPension ? amount : 0
        return {
          ...item,
          rowData: createData(
            deductionSubTypeLabelMap[subType],
            amount,
            (((amount / 80) * 2087) / 12) * timeFactor,
            (((retirementAmount / 80) * 2087) / 12) * timeFactor,
            ((((retirementAmount - amount) / 80) * 2087) / 12) * timeFactor,
            '',
            '-'
          ),
        }
      }),
    ]

    // const liabilitiesMap = [
    //   ...liabilities
    //     .filter((liability) => liability.subType === 'tspLoan')
    //     .map(({ name, amount, type, loanEndDate, monthlyPayment }) => {
    //       return createData(
    //         name,
    //         monthlyPayment,
    //         ((monthlyPayment * 26) / 12) * timeFactor,
    //         ((monthlyPayment * 26) / 12) * timeFactor,
    //         0
    //       )
    //     }),
    // ]

    const incomesAndExpenses = [...incomes, ...expenses]
    const workingIncomesExpenses = [
      ...incomesAndExpenses
        .filter((item) => {
          if (item.subType === 'tspLoan') return
          if (!item.ieStartDate) return true
          const today = new Date()
          const startDate = new Date(item.ieStartDate)
          return startDate <= today
        })
        .map((item) => {
          const { amount, frequency, type, monthlyPayment, subType } = item
          const typeAdjustmentAmount =
            type === 'expense'
              ? -amount
              : subType === 'tspLoan'
              ? ((-(monthlyPayment || 0) / 80) * 2087) / 12
              : type === 'liability'
              ? -(monthlyPayment || 0) || 0
              : type === 'assetIncome'
              ? monthlyPayment || 0
              : amount
          const adjustedPerMonth =
            type === 'liability'
              ? typeAdjustmentAmount
              : frequency === 'monthly'
              ? typeAdjustmentAmount
              : typeAdjustmentAmount / 12
          return {
            ...item,
            rowData: createData(
              item.name,
              ((adjustedPerMonth * 12) / 2087) * 80,
              adjustedPerMonth * timeFactor,
              adjustedPerMonth * timeFactor,
              0,
              '',
              type === 'expense' || type === 'liability' ? '-' : '+',
              '',
              type
            ),
          }
        }),
    ]
    // console.log('workingIncomesExpenses', workingIncomesExpenses)
    const retirementIncomesExpenses = [
      ...incomesAndExpenses
        .filter((item) => {
          if (!item.ieStartDate) return true
          const retirementDate = new Date(plannedRetirementDate)
          const startDate = new Date(item.ieStartDate)
          const today = new Date()
          return startDate <= retirementDate || startDate <= today
        })
        .map((item) => {
          const { amount, frequency, type, subType, monthlyPayment } = item
          const typeAdjustmentAmount =
            type === 'expense'
              ? -amount
              : subType === 'tspLoan'
              ? ((-(monthlyPayment || 0) / 80) * 2087) / 12
              : type === 'liability'
              ? -(monthlyPayment || 0) || 0
              : type === 'assetIncome'
              ? monthlyPayment || 0
              : amount
          const adjustedPerMonth =
            type === 'liability'
              ? typeAdjustmentAmount
              : frequency === 'monthly'
              ? typeAdjustmentAmount
              : typeAdjustmentAmount / 12
          return {
            ...item,
            rowData: createData(
              item.name,
              ((adjustedPerMonth * 12) / 2087) * 80,
              adjustedPerMonth * timeFactor,
              adjustedPerMonth * timeFactor,
              0,
              '',
              type === 'expense' || type === 'liability' ? '-' : '+',
              '',
              type
            ),
          }
        }),
    ]
    // console.log('retirementIncomesExpenses', retirementIncomesExpenses)
    const iEAdjustmentWorkingTotal = workingIncomesExpenses.reduce(
      (sum, { rowData }) => sum + Number(rowData.working),
      0
    )
    // console.log('iEAdjustmentWorkingTotal', iEAdjustmentWorkingTotal)
    const iEAdjustmentRetiredTotal = retirementIncomesExpenses.reduce(
      (sum, { rowData }) => sum + Number(rowData.working),
      0
    )
    // console.log('iEAdjustmentRetiredTotal', iEAdjustmentRetiredTotal)
    const cashAddbackTotals = deductions
      .filter(({ subType }) => ['alotchecking', 'fsa', 'hsa'].includes(subType))
      .map(({ amount, subType, keepInRetirement }) => {
        const retirementAmount = keepInRetirement ? amount : 0
        const paycheck = amount
        const working = ((amount / 80) * 2087) / 12
        const retirement = ((retirementAmount / 80) * 2087) / 12
        const difference = (((retirementAmount - amount) / 80) * 2087) / 12
        return {
          paycheck,
          working,
          retirement,
          difference,
        }
      })
      .reduce(
        (sum, { paycheck, working, retirement, difference }) => {
          sum.paycheck += +paycheck
          sum.working += +working
          sum.retirement += +retirement
          sum.difference += +difference
          return sum
        },
        { paycheck: 0, working: 0, retirement: 0, difference: 0 }
      )

    otherDeductions.push(
      createData(
        'Pension Contributions',
        pensionPaycheckContributionAmount,
        pensionContributionAmount,
        0,
        0 - pensionContributionAmount
        // 'hideLine'
      )
    )
    otherDeductions.push(
      createData(
        'TSP Contributions',
        tspPaycheckContributionAmount,
        tspContributionAmount,
        0,
        0 - tspContributionAmount
        // 'hideLine'
      )
    )
    const survivorBenefitRow =
      survivorBenefitCost > 0 &&
      isGovernmentEmployee &&
      otherDeductions.push(
        createData(
          'Survivor Benefit',
          0,
          0,
          survivorBenefitCost * timeFactor,
          survivorBenefitCost * timeFactor,
          'https://upload.wikimedia.org/wikipedia/commons/0/0a/Seal_of_the_United_States_Office_of_Personnel_Management.svg',
          '-'
        )
      )
    const healthInsuranceRow =
      showPension &&
      healthInsurancePaycheckCost &&
      otherDeductions.push(
        createData(
          'Health Insurance',
          healthInsurancePaycheckCost,
          healthInsuranceCost * timeFactor,
          healthInsuranceRetirementCost * timeFactor,
          0,
          healthcareLogo || '',
          '-'
        )
      )
    const basicLifeRow =
      showPension &&
      basicLife &&
      otherDeductions.push(
        createData(
          'Basic Life',
          basicLifePaycheckCost,
          basicLifeCost * timeFactor,
          basicLifeRetirementCost * timeFactor,
          (basicLifeRetirementCost - basicLifeCost) * timeFactor,
          fegliLogo,
          '-'
        )
      )
    const optionARow =
      showPension &&
      optionA &&
      otherDeductions.push(
        createData(
          'Option A',
          optionAPaycheckCost,
          optionACost * timeFactor,
          optionARetirementCost * timeFactor,
          (optionARetirementCost - optionACost) * timeFactor,
          fegliLogo,
          '-'
        )
      )
    const optionBRow =
      showPension &&
      optionB &&
      otherDeductions.push(
        createData(
          'Option B',
          optionBPaycheckCost,
          optionBCost * timeFactor,
          optionBRetirementCost * timeFactor,
          (optionBRetirementCost - optionBCost) * timeFactor,
          fegliLogo,
          '-'
        )
      )
    const optionCRow =
      showPension &&
      optionC &&
      otherDeductions.push(
        createData(
          'Option C',
          optionCPaycheckCost,
          optionCCost * timeFactor,
          optionCRetirementCost * timeFactor,
          (optionCRetirementCost - optionCCost) * timeFactor,
          fegliLogo,
          '-'
        )
      )
    // const estimatedTaxesRow =
    //   estimatedTaxesPaycheckWhileWorking &&
    //   otherDeductions.push(
    //     createData(
    //       'Estimated Taxes',
    //       dollarFormat.format(estimatedTaxesPaycheckWhileWorking),
    //       dollarFormat.format(estimatedTaxesWhileWorking),
    //       dollarFormat.format(estimatedTaxesInRetirement),
    //       dollarFormat.format(estimatedTaxesDifference)
    //     )
    //   )

    // console.log(otherDeductions)

    // console.log(otherDeductions)

    const deductionOrder = [
      'Survivor Benefit',
      'Pension Contributions',
      'TSP Contributions',
      'Health Insurance',
      'Basic Life',
      'Option A',
      'Option B',
      'Option C',
    ]

    const orderedOtherDeductions = reorderDeductions(
      otherDeductions,
      deductionOrder,
      'name'
    )

    const otherDeductionTotals = otherDeductions.reduce(
      (summ, deduction) => {
        const data = deduction.rowData || deduction
        summ.paycheck += +(data.paycheck || 0)
        summ.working += +(data.working || 0)
        summ.retirement += +(data.retirement || 0)
        summ.difference += +(data.difference || 0)
        return summ
      },
      { paycheck: 0, working: 0, retirement: 0, difference: 0 }
    )

    const retirmentIncomeItems = [
      ...initialSources,
      ...retirementIncomesExpenses.filter(
        (item) => item.type === 'income' || item.type === 'assetIncome'
      ),
      ...otherDeductions,
      ...totalTaxes,
      ...retirementIncomesExpenses.filter(
        (item) => item.type === 'expense' || item.type === 'liability'
      ),
    ].filter((item) => {
      if (!isGovernmentEmployee) {
        if (item.name.toLowerCase().includes('pension')) {
          return false
        }
        if (item.name.toLowerCase().includes('fers supplement')) {
          return false
        }
      }
      const retirementVal = item.rowData?.retirement ?? item.retirement ?? 0
      return retirementVal !== 0
    })

    // net take-home pay OLD
    // const estimatedNetTakeHomePaycheckWhileWorking =
    //   littleGPaycheckAmount -
    //   otherDeductionTotals.paycheck -
    //   estimatedTaxesPaycheckWhileWorking +
    //   //add back in the contributions to TSP and Pension to compensate for the table adjustment
    //   (pensionPaycheckContributionAmount + tspPaycheckContributionAmount)
    // const estimatedNetTakeHomeWhileWorking =
    //   littleGAmount -
    //   otherDeductionTotals.working -
    //   estimatedTaxesWhileWorking +
    //   //add back the contributions to TSP and Pension to compensate for the table adjustment
    //   (pensionContributionAmount + tspContributionAmount)
    const estimatedNetTakeHomeInRetirement =
      normalizedGrossIncomeRetirement -
      otherDeductionTotals.retirement -
      estimatedTaxesInRetirement
    // Adjusted Totals
    const adjustedNetTakeHomeInRetirement =
      estimatedNetTakeHomeInRetirement +
      cashAddbackTotals.retirement +
      iEAdjustmentRetiredTotal

    const adjustedWorkingNetTakeHome =
      workingNetTakeHome +
      cashAddbackTotals.working +
      (intakeState?.alreadyCollectingSocialSecurity
        ? intakeState?.estimatedSocialSecurity
        : 0) +
      iEAdjustmentWorkingTotal

    const adjustedNetTakeHomeDifferencePARCOGap =
      adjustedWorkingNetTakeHome - adjustedNetTakeHomeInRetirement
    return {
      cashAddbackTotals,
      retirmentIncomeItems,
      adjustedNetTakeHomeInRetirement,
      workingNetTakeHome,
      adjustedWorkingNetTakeHome,
      adjustedNetTakeHomeDifferencePARCOGap,
      workingIncomesExpenses,
      retirementIncomesExpenses,
    }
  } catch (e) {
    console.log(e)
    return {}
  }
}
